import { ConnectProto } from "@racoon/api";
import { fromTimestamp } from "@racoon/lib";

/** A job's photo reports, split by what stage of the service they document. */
export type JobPhotos = {
  beforePhotos: string[];
  afterPhotos: string[];
  failureReasonPhotos: string[];
};

/** The stage of the service a photo documents. */
export type JobPhotoGroup = "before" | "after" | "failure";

/** One photo of a job's report, in the order the photo viewer pages through them. */
export type JobPhotoSlide = {
  url: string;
  group: JobPhotoGroup;
  /** When the file reached the server — not when it was taken (the driver app queues offline). */
  uploadedAt: Date | null;
};

type JobWithReports = { fileReports?: any[]; [key: string]: any } | null | undefined;

const GROUP_ORDER: JobPhotoGroup[] = ["before", "after", "failure"];

function photoGroupOf(type: unknown): JobPhotoGroup | null {
  switch (type) {
    case ConnectProto.FileUploadType.JOB_SERVICE_STARTED:
    case ConnectProto.FileUploadType.CONTAINER_BEFORE:
      return "before";
    case ConnectProto.FileUploadType.JOB_SERVICE_ENDED:
    case ConnectProto.FileUploadType.CONTAINER_AFTER:
      return "after";
    case ConnectProto.FileUploadType.ROUTE_TASK_FAILURE_REASON:
      return "failure";
    default:
      return null;
  }
}

/**
 * Every photo of a job as one list: "before", then "after", then failure-reason
 * photos, each group in upload order. Reports without a url or of an unrelated
 * FileUploadType are left out.
 */
export function getJobPhotoSlides(job: JobWithReports): JobPhotoSlide[] {
  const reports: any[] = job?.fileReports ?? [];
  const slides = reports.flatMap((report): JobPhotoSlide[] => {
    const group = photoGroupOf(report?.type);
    if (!report?.url || !group) return [];
    return [{ url: report.url, group, uploadedAt: fromTimestamp(report.createdAt) }];
  });
  return GROUP_ORDER.flatMap((group) => slides.filter((slide) => slide.group === group));
}

/**
 * Splits a job's `fileReports` into "before" (service started), "after"
 * (service ended) and failure-reason photos using their FileUploadType,
 * keeping only entries that carry a url.
 */
export function getJobPhotos(job: JobWithReports): JobPhotos {
  const urlsOf = (slides: JobPhotoSlide[], group: JobPhotoGroup) =>
    slides.filter((slide) => slide.group === group).map((slide) => slide.url);
  const slides = getJobPhotoSlides(job);
  return {
    beforePhotos: urlsOf(slides, "before"),
    afterPhotos: urlsOf(slides, "after"),
    failureReasonPhotos: urlsOf(slides, "failure"),
  };
}
